export interface NavItem {
  label: string;
  shortLabel?: string;
  icon: string;
  path: string;
  requiresPremium?: boolean;
}

export type EtfBackTarget = {
  pathname: string;
  search: string;
  label: string;
};

export type EtfNavigationState = {
  backTo?: Partial<EtfBackTarget> | null;
};

export const featureNavItems: NavItem[] = [
  { label: 'Daily Pulse', icon: '📰', path: '/pulse' },
  { label: 'Market Foundations', shortLabel: 'Learn', icon: 'MF', path: '/market-foundations' },
  { label: 'Indonesia Equities', icon: '🇮🇩', path: '/indonesian-equities' },
  { label: 'Expected Return', icon: '🎯', path: '/expected-return' },
  { label: 'Weekend Movers', icon: '🌙', path: '/weekend-movers' },
  { label: 'Strategies', icon: '🧪', path: '/strategies' },
  { label: 'Options Finder', icon: '🎲', path: '/options-analyzer' },
  { label: 'Market Context', icon: '🗺️', path: '/market-context' },
  { label: 'Fed Watch', icon: '🏦', path: '/fed-watch' },
  { label: 'Trade Log', icon: '📝', path: '/trade-log' },
];

export const thesisTrackerNavItems: NavItem[] = [
  {
    label: "Cem Karsan's Summer of George Thesis",
    icon: '📊',
    path: '/thesis-tracker/cem-karsan-summer-2026-thesis',
  },
  {
    label: 'Serenity AI Bubble Hero Thesis',
    icon: 'S',
    path: '/thesis-tracker/serenity-ai-bubble-hero-thesis',
  },
  { label: 'Build your own thesis', icon: '✍️', path: '/thesis-tracker/build' },
];

export const dashboardNavItems: NavItem[] = [
  { label: 'Cross-Asset Class', shortLabel: 'Cross-Asset', icon: '📋', path: '/' },
  { label: 'Macro', shortLabel: 'Macro', icon: '🧭', path: '/dashboards/macro', requiresPremium: true },
  { label: 'Bonds', icon: '🏛️', path: '/dashboards/bonds', requiresPremium: true },
  { label: 'Commodities', shortLabel: 'Commodities', icon: '⛏️', path: '/dashboards/commodities', requiresPremium: true },
  { label: 'Currencies', shortLabel: 'FX', icon: '💱', path: '/dashboards/currencies', requiresPremium: true },
  { label: 'Crypto', icon: '₿', path: '/dashboards/crypto', requiresPremium: true },
];

export const equityDashboardNavItems: NavItem[] = [
  {
    label: 'Global (Countries MSCI ETFs)',
    shortLabel: 'Global (Countries MSCI ETFs)',
    icon: '🌍',
    path: '/dashboards/equities/global',
    requiresPremium: true,
  },
  { label: 'Thematic ETFs', shortLabel: 'Thematic ETFs', icon: '🧩', path: '/dashboards/equities/thematic', requiresPremium: true },
  { label: 'S&P 500 Sectors', shortLabel: 'S&P 500 Sectors', icon: '🇺🇸', path: '/dashboards/equities/us', requiresPremium: true },
];

export const timeSeriesMomentumNavItems: NavItem[] = [
  { label: 'US Equities', icon: '🇺🇸', path: '/us-equities' },
];

export const bottomTabItems: NavItem[] = [
  { label: 'Market Analytics', icon: '📋', path: '/' },
  { label: 'Market Foundations', shortLabel: 'Learn', icon: 'MF', path: '/market-foundations' },
  { label: 'US Equities', icon: '🇺🇸', path: '/us-equities' },
  { label: 'Strategies', icon: '🧪', path: '/strategies' },
  { label: 'Context', icon: '🗺️', path: '/market-context' },
  { label: 'Movers', icon: '🌙', path: '/weekend-movers' },
  { label: 'Fed Watch', shortLabel: 'Fed', icon: '🏦', path: '/fed-watch' },
  {
    label: "Cem Karsan's Summer of George Thesis",
    shortLabel: 'Thesis',
    icon: '📊',
    path: '/thesis-tracker/cem-karsan-summer-2026-thesis',
  },
  { label: 'Trade Log', icon: '📝', path: '/trade-log' },
];

export const isDashboardRoute = (pathname: string): boolean =>
  pathname === '/' || pathname.startsWith('/dashboards/') || pathname.startsWith('/etf/');

export const isPremiumDashboardRoute = (pathname: string): boolean =>
  pathname !== '/' && (pathname.startsWith('/dashboards/') || pathname.startsWith('/etf/'));

export const getTopbarTitle = (pathname: string): string => {
  if (
    pathname === '/pulse' ||
    pathname === '/pulse-editorial-experiment' ||
    pathname === '/dailypulse' ||
    pathname === '/dailypulseold' ||
    pathname === '/newsletter'
  ) {
    return 'Daily Pulse';
  }
  if (pathname === '/rankings' || pathname === '/derby') {
    return 'Matrix Rankings';
  }
  if (pathname === '/options-analyzer' || pathname === '/options-preview' || pathname.startsWith('/options-analyzer/')) {
    return 'Options Finder';
  }
  if (pathname === '/market-foundations' || pathname.startsWith('/market-foundations/')) {
    return 'Market Foundations';
  }
  if (pathname === '/us-equities' || pathname.startsWith('/us-equities/')) {
    return 'US Equities';
  }
  if (pathname === '/indonesian-equities' || pathname.startsWith('/indonesian-equities/')) {
    return 'Indonesia Equities';
  }
  if (pathname === '/expected-return' || pathname.startsWith('/expected-return/')) {
    return 'Expected Return';
  }
  if (pathname === '/weekend-movers' || pathname.startsWith('/weekend-movers/')) {
    return 'Weekend Movers';
  }
  if (pathname === '/strategies' || pathname.startsWith('/strategies/')) {
    return 'Strategies';
  }
  if (pathname === '/market-context' || pathname.startsWith('/market-context/')) {
    return 'Market Context';
  }
  if (pathname === '/fed-watch' || pathname.startsWith('/fed-watch/')) {
    return 'Fed Watch';
  }
  if (pathname === '/cem-tracker' || pathname === '/thesis-tracker/cem-karsan-summer-2026-thesis') {
    return "Cem Karsan's Summer of George Thesis";
  }
  if (pathname === '/thesis-tracker/serenity-ai-bubble-hero-thesis') {
    return 'Serenity AI Bubble Hero Thesis';
  }
  if (pathname === '/thesis-tracker/build') {
    return 'Build your own thesis';
  }
  if (pathname === '/trade-log' || pathname.startsWith('/trade-log/')) {
    return 'Trade Log';
  }
  if (pathname === '/account/subscription' || pathname === '/my-subscriptions') {
    return 'My Subscription';
  }
  if (pathname === '/polymarket') {
    return 'Polymarket';
  }
  if (pathname === '/admin') {
    return 'Admin';
  }
  if (pathname === '/dashboards/bonds') {
    return 'Bonds Dashboard';
  }
  if (pathname === '/dashboards/macro') {
    return 'Macro Dashboard';
  }
  if (pathname === '/dashboards/allocation-history') {
    return 'Historical Allocation';
  }
  if (pathname === '/dashboards/commodities') {
    return 'Commodities Dashboard';
  }
  if (pathname === '/dashboards/commodities/cpo1') {
    return 'Crude Palm Oil';
  }
  if (pathname === '/dashboards/currencies') {
    return 'Currencies Dashboard';
  }
  if (pathname === '/dashboards/crypto') {
    return 'Crypto Dashboard';
  }
  if (pathname === '/dashboards/equities/global') {
    return 'Global (Countries MSCI ETFs) Dashboard';
  }
  if (pathname === '/dashboards/equities/thematic') {
    return 'Thematic ETFs Dashboard';
  }
  if (pathname === '/dashboards/equities/us') {
    return 'S&P 500 Sectors Dashboard';
  }
  if (pathname.startsWith('/etf/')) {
    return 'ETF Components';
  }
  if (pathname === '/login') {
    return 'Log In';
  }
  if (pathname === '/forgot-password') {
    return 'Reset Password';
  }
  if (pathname === '/login-callback') {
    return 'Completing Login';
  }
  return 'Market Analytics';
};

const DEFAULT_ETF_BACK_TARGET: EtfBackTarget = {
  pathname: '/',
  search: '',
  label: 'Cross-Asset',
};

const normalizeBackLabel = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return DEFAULT_ETF_BACK_TARGET.label;
  return trimmed.endsWith(' Dashboard') ? trimmed.slice(0, -' Dashboard'.length) : trimmed;
};

export const getEtfBackLabelForPath = (pathname: string): string => {
  if (pathname === '/') return DEFAULT_ETF_BACK_TARGET.label;
  if (pathname.startsWith('/strategies/')) return 'Strategy';
  return normalizeBackLabel(getTopbarTitle(pathname));
};

export const buildEtfNavigationState = ({
  pathname,
  search = '',
  label,
}: {
  pathname: string;
  search?: string;
  label?: string;
}): EtfNavigationState => ({
  backTo: {
    pathname: pathname || DEFAULT_ETF_BACK_TARGET.pathname,
    search,
    label: normalizeBackLabel(label || getEtfBackLabelForPath(pathname)),
  },
});

export const resolveEtfBackTarget = (state: unknown): EtfBackTarget => {
  const backTo = (state as EtfNavigationState | null | undefined)?.backTo;
  if (!backTo || typeof backTo !== 'object') return DEFAULT_ETF_BACK_TARGET;

  const pathname =
    typeof backTo.pathname === 'string' && backTo.pathname.trim()
      ? backTo.pathname
      : DEFAULT_ETF_BACK_TARGET.pathname;
  const search = typeof backTo.search === 'string' ? backTo.search : DEFAULT_ETF_BACK_TARGET.search;
  const label =
    typeof backTo.label === 'string' && backTo.label.trim()
      ? normalizeBackLabel(backTo.label)
      : getEtfBackLabelForPath(pathname);

  return {
    pathname,
    search,
    label,
  };
};
